import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React from 'react';
import PropTypes from 'prop-types';
import Globalize from '@sprinx/globalize';
import InputAdornment from '@material-ui/core/InputAdornment';
import NumberField from '../NumberField';
import useMuiFieldsContext from '../useMuiFieldsContext';
import CurrencySymbol from '../CurrencySymbol';

function PriceField(_ref) {
  var pcur = _ref.currency,
      pcf = _ref.currencyFormat,
      pdp = _ref.decimalPrecision,
      rest = _objectWithoutProperties(_ref, ["currency", "currencyFormat", "decimalPrecision"]);

  var _useMuiFieldsContext = useMuiFieldsContext(),
      currencyDecimalPrecision = _useMuiFieldsContext.currencyDecimalPrecision,
      currencyFormat = _useMuiFieldsContext.currencyFormat,
      defaultCurrency = _useMuiFieldsContext.defaultCurrency;

  var decimalPrecision = pdp || currencyDecimalPrecision;
  var currency = pcur || defaultCurrency;
  var curF = pcf || currencyFormat;
  var symbolplacement = Globalize.locale().main('numbers/currencyFormats-numberSystem-latn/standard');
  var InputProps = /^¤/.test(symbolplacement) ? {
    startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "start"
    }, /*#__PURE__*/React.createElement(CurrencySymbol, {
      currency: currency,
      variant: curF
    }))
  } : {
    endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
      position: "end"
    }, /*#__PURE__*/React.createElement(CurrencySymbol, {
      currency: currency,
      variant: curF
    }))
  };
  return /*#__PURE__*/React.createElement(NumberField, _extends({}, rest, {
    decimalPrecision: decimalPrecision,
    InputProps: InputProps
  }));
}

PriceField.propTypes = {
  allowNegative: PropTypes.bool,

  /**
   * This property helps users to fill forms faster, especially on mobile devices.
   * The name can be confusing, as it's more like an autofill. You can learn more about it here:
   * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill
   */
  autoComplete: PropTypes.string,

  /**
   * `true` ak ma mat pole focus po prvom mounte.
   */
  autoFocus: PropTypes.bool,

  /**
   * Jmeno CSS tridy.
   */
  className: PropTypes.string,

  /**
   * Kod meny, ve ktere je hodnota. Pokud neni definovana, pouzije se nastaveni z MuiFieldsProvider.
   */
  currency: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    code: PropTypes.string.isRequired
  })]),

  /**
   * Format zobrazeni meny. Pokud neni definovano pouzije se nastaveni z MuiFieldsProvider.
   * - 'accounting' -> XXX Kč
   * - 'code' -> XXX CZK
   */
  currencyFormat: PropTypes.oneOf(['accounting', 'code']),

  /**
   * Pocet desetinnych mist.
   * Pokud neni definovana pouzite se nastaveni z Provideru.
   */
  decimalPrecision: PropTypes.number,

  /**
   * Defaultni hodnota.
   */
  defaultValue: PropTypes.string,

  /**
   * `true` pokud ma byt field disablovan.
   */
  disabled: PropTypes.bool,

  /**
   * Chybova hlaska.
   */
  error: PropTypes.string,

  /**
   * Pokud je `true` bude se renderovat polozka na plnou dostupnou sirku.
   */
  fullWidth: PropTypes.bool,

  /**
   * Pomocny text, zobrazovany pod `input`em.
   */
  helperText: PropTypes.string,

  /**
   * Hodnota atributu `id` nativniho `input`u.
   */
  id: PropTypes.string,

  /**
   * `Object` s custom vlastnostmi komponenty `InputLabel`.
   */
  InputLabelProps: PropTypes.shape({}),

  /**
   * `Object` s custom vlastnostmi komponenty `Input`.
   */
  InputProps: PropTypes.shape({}),

  /**
   * `Object` s custom vlastnostmi komponenty `input` (nativni).
   */
  inputProps: PropTypes.shape({}),

  /**
   * `Ref` inputu.
   */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),

  /**
   * Zobrazovany label.
   */
  label: PropTypes.string,

  /**
   * Margin fieldu.
   */
  margin: PropTypes.oneOf(['none', 'dense', 'normal']),

  /**
   * Hodnota atributu `name` nativniho `input`u.
   */
  name: PropTypes.string,

  /**
   * Handler pro obsluhu zmeny.
   *
   * Signatura funkce: `(value: TValue) => void`
   */
  onChange: PropTypes.func.isRequired,

  /**
   * Hodnota zobrazovana, pokud je `value` prazdne / nedefinovane.
   */
  placeholder: PropTypes.string,

  /**
   * `true` pokud ma byt field vyzadovan. Pokud je `true`, automaticky se do labelu prida '*'.
   */
  required: PropTypes.bool,

  /**
   * Objekt stylu.
   */
  style: PropTypes.shape({}),

  /**
   * Hodnota atributu `type` nativniho `input`u.
   */
  type: PropTypes.string,

  /**
   * Hodnota fieldu (`TValue`).
   */
  value: PropTypes.number
};
PriceField.defaultProps = {
  allowNegative: true,
  autoComplete: undefined,
  autoFocus: undefined,
  className: undefined,
  currency: undefined,
  currencyFormat: undefined,
  decimalPrecision: undefined,
  defaultValue: undefined,
  disabled: undefined,
  error: undefined,
  fullWidth: undefined,
  helperText: undefined,
  id: undefined,
  InputLabelProps: undefined,
  InputProps: undefined,
  inputProps: undefined,
  inputRef: undefined,
  label: undefined,
  margin: undefined,
  name: undefined,
  placeholder: undefined,
  required: undefined,
  style: undefined,
  type: undefined,
  value: undefined
};
export default PriceField;